import { Ref, computed, ComputedRef } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { get, put } from './helpers'
import type {
  ApiAlertResponseType,
  ApiAlertGroupedByProperty,
  AlertPutRequestType
} from '@/interfaces'

interface UseQueryResponse {
  isPending: Ref<boolean>
  isSuccess: Ref<boolean>
  isError: Ref<boolean>
  data: Ref<ApiAlertResponseType[] | [] | undefined>
  error: Ref<Error | null>
  refetch: () => void
}

interface UseAlertsQueryResponse extends UseQueryResponse {
  groupedData: ComputedRef<ApiAlertGroupedByProperty>
}

const TWO_MINUTES = 1000 * 60 * 2

export const useAlertsQuery = (options?: {
  enabled?: boolean | Ref<boolean>
}): UseAlertsQueryResponse => {
  const router = useRouter()
  const enabled = options?.enabled ?? true
  const {
    isPending,
    isSuccess,
    isError,
    data,
    error,
    refetch
  }: UseQueryResponse = useQuery({
    queryKey: ['alerts'],
    queryFn: async () => get(`alerts`, router),
    staleTime: TWO_MINUTES,
    refetchInterval: TWO_MINUTES,
    enabled
  })

  const groupedData: ComputedRef<ApiAlertGroupedByProperty> = computed(() => {
    let groupResult = {}
    if (data.value) {
      groupResult = data.value.reduce((acc, curr) => {
        const { property_id, ...rest } = curr
        if (!acc[property_id]) {
          acc[property_id] = []
        }
        acc[property_id].push(rest)
        return acc
      }, {} as ApiAlertGroupedByProperty)
    }
    return groupResult
  })

  return { isPending, isSuccess, isError, data, groupedData, error, refetch }
}

interface UseAlertsMutationProps {
  unitId: number
  propertyId: number
  onSuccessCallback?: (response: ApiAlertResponseType) => void
  onErrorCallback?: (error: unknown) => void
}

export const useAlertsMutation = ({
  unitId,
  propertyId,
  onSuccessCallback,
  onErrorCallback
}: UseAlertsMutationProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()

  const mutation = useMutation<
    ApiAlertResponseType,
    Error,
    AlertPutRequestType
  >({
    mutationFn: (body) =>
      put(`/alerts/${unitId}`, body, router, propertyId.toString()),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: ['alerts']
      })
      if (onSuccessCallback) {
        onSuccessCallback(response)
      }
    },
    onError: () => {
      if (onErrorCallback) {
        onErrorCallback(mutation.error)
      }
    }
  })

  function resolveLeak(body: AlertPutRequestType) {
    mutation.mutate(body)
  }

  return {
    ...mutation,
    resolveLeak
  }
}
